import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Close, KeyboardArrowDown, Search, Tune } from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DEFAULT_PROFITABILITY_METHOD,
  ProfitabilityFilter,
  State,
} from 'src/types/profitability';
import { useDebounce } from 'src/utils/hooks';

type ProfitabilityFiltersProps = {
  filters?: ProfitabilityFilter;
  state: State;
  setState: Dispatch<SetStateAction<State>>;
  disabled: boolean;
  isLoading: boolean;
};

const NOT_NULL_FILTERS = ['mercado', 'metodo'];
const PRIMARY_FILTER_CODE = 'ativo_lastro';
const ADVANCED_FILTER_ORDER = [
  'metodo',
  'tipo_devedor',
  'pulverizacao',
  'classificacao_risco',
];

const isEmptyFilterValue = (value: State[keyof State]) =>
  value === '' || value === null || value === undefined;

export function ProfitabilityFilters({
  filters,
  state,
  setState,
  disabled,
  isLoading,
}: ProfitabilityFiltersProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [searchValue, setSearchValue] = useState(state.search);
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const ignorePendingDebouncedSearchRef = useRef(false);

  const primaryFilter = filters?.[PRIMARY_FILTER_CODE];
  const advancedFilters = useMemo(() => {
    if (!filters) return [];

    const orderedFilters = ADVANCED_FILTER_ORDER.flatMap((filterCode) =>
      filters[filterCode] ? [filters[filterCode]] : [],
    );
    const remainingFilters = Object.entries(filters)
      .filter(
        ([filterCode]) =>
          filterCode !== 'mercado' &&
          filterCode !== 'ativo' &&
          filterCode !== PRIMARY_FILTER_CODE &&
          !ADVANCED_FILTER_ORDER.includes(filterCode),
      )
      .map(([, filter]) => filter)
      .sort((first, second) => first.label.localeCompare(second.label));

    return [...orderedFilters, ...remainingFilters];
  }, [filters]);

  const isFilterActive = (
    filterCode: keyof State,
    value: State[keyof State],
  ) => {
    if (filterCode === 'metodo') {
      return value !== DEFAULT_PROFITABILITY_METHOD;
    }

    return !isEmptyFilterValue(value);
  };

  const activePrimaryFilters = primaryFilter
    ? [primaryFilter].filter(({ code }) =>
        isFilterActive(code as keyof State, state[code as keyof State]),
      )
    : [];
  const activeAdvancedFilters = advancedFilters.filter(({ code }) =>
    isFilterActive(code as keyof State, state[code as keyof State]),
  );
  const activeFilters = [...activePrimaryFilters, ...activeAdvancedFilters];
  const activeSearch = Boolean(state.search);
  const hasActiveFilters = activeSearch || activeFilters.length > 0;

  useEffect(() => {
    setSearchValue((currentSearchValue) => {
      if (state.search === currentSearchValue) return currentSearchValue;

      ignorePendingDebouncedSearchRef.current = true;
      return state.search;
    });
  }, [state.search]);

  useEffect(() => {
    if (ignorePendingDebouncedSearchRef.current) {
      if (debouncedSearchValue === state.search) {
        ignorePendingDebouncedSearchRef.current = false;
      }

      return;
    }

    if (debouncedSearchValue === state.search) return;

    setState((currentState) => {
      if (currentState.search === debouncedSearchValue) return currentState;

      return { ...currentState, search: debouncedSearchValue };
    });
  }, [debouncedSearchValue, setState, state.search]);

  const handleFilterChange = (
    filterCode: string,
    value: State[keyof State],
  ) => {
    setState((currentState: State) => {
      if (filterCode === 'mercado') {
        return {
          mercado: String(value),
          search: currentState.search,
          ativo_lastro: '',
          metodo: DEFAULT_PROFITABILITY_METHOD,
          tipo_devedor: '',
          pulverizacao: '',
          classificacao_risco: '',
        };
      }

      return { ...currentState, [filterCode]: value };
    });
  };

  const handleClearFilters = () => {
    setState((currentState) => ({
      ...currentState,
      search: '',
      ativo_lastro: '',
      metodo: DEFAULT_PROFITABILITY_METHOD,
      tipo_devedor: '',
      pulverizacao: '',
      classificacao_risco: '',
    }));
  };

  const handleClearFilter = (filterCode: keyof State) => {
    setState((currentState) => {
      if (filterCode === 'metodo') {
        return { ...currentState, metodo: DEFAULT_PROFITABILITY_METHOD };
      }

      return { ...currentState, [filterCode]: '' };
    });
  };

  const activeChips = [
    activeSearch
      ? {
          key: 'search',
          label: `Busca: ${state.search}`,
          onDelete: () => handleClearFilter('search'),
        }
      : null,
    ...activeFilters.map((filter) => {
      const currentValue = state[filter.code as keyof State];
      const selectedOption = filter.options.find(
        ({ value }) => value === currentValue,
      );

      return {
        key: filter.code,
        label: `${filter.label}: ${selectedOption?.label ?? currentValue}`,
        onDelete: () => handleClearFilter(filter.code as keyof State),
      };
    }),
  ].filter(Boolean) as Array<{
    key: string;
    label: string;
    onDelete: () => void;
  }>;

  return (
    <Box
      sx={{
        width: '100%',
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        backgroundColor: 'background.paper',
      }}>
      {isLoading ? (
        <Skeleton variant="rounded" width="100%" height={112} />
      ) : (
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={1}>
            <Box>
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', letterSpacing: 1.2 }}>
                Filtros
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ativo e busca em destaque; filtros complementares ficam
                recolhidos.
              </Typography>
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ pt: 0.5 }}>
              {hasActiveFilters
                ? `${activeFilters.length + Number(activeSearch)} ativo(s)`
                : 'Nenhum filtro ativo'}
            </Typography>
          </Stack>

          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: 'column', lg: 'row' }}
              spacing={1.5}
              alignItems={{ lg: 'flex-start' }}>
              {primaryFilter ? (
                <Box sx={{ width: { xs: '100%', sm: 220 }, flexShrink: 0 }}>
                  <FormControl fullWidth size="small" disabled={disabled}>
                    <InputLabel id={`${primaryFilter.code}-primary-label`}>
                      {primaryFilter.label}
                    </InputLabel>
                    <Select
                      labelId={`${primaryFilter.code}-primary-label`}
                      id={`${primaryFilter.code}-primary-select`}
                      label={primaryFilter.label}
                      disabled={disabled || !primaryFilter.options.length}
                      value={state[primaryFilter.code as keyof State]}
                      onChange={(event) =>
                        handleFilterChange(
                          primaryFilter.code,
                          event.target.value as State[keyof State],
                        )
                      }>
                      {!NOT_NULL_FILTERS.includes(primaryFilter.code) && (
                        <MenuItem value="">Selecione</MenuItem>
                      )}
                      {primaryFilter.options.map(({ label, value }) => (
                        <MenuItem
                          key={String(value)}
                          value={value as unknown as string}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              ) : null}

              <TextField
                fullWidth
                size="small"
                label="Busca"
                placeholder="Buscar por nome ou ticker"
                value={searchValue}
                disabled={disabled}
                onChange={(event) => setSearchValue(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchValue ? (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        size="small"
                        aria-label="Limpar busca"
                        onClick={() => setSearchValue('')}
                        disabled={disabled}>
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined,
                }}
              />

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ width: { xs: '100%', lg: 'auto' } }}>
                {hasActiveFilters ? (
                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    onClick={handleClearFilters}
                    disabled={disabled}
                    startIcon={<Close fontSize="small" />}>
                    Limpar
                  </Button>
                ) : null}

                <Tooltip
                  title={advancedOpen ? 'Ocultar filtros' : 'Mostrar filtros'}>
                  <span>
                    <Badge
                      color="primary"
                      badgeContent={activeAdvancedFilters.length}
                      invisible={!activeAdvancedFilters.length}
                      overlap="circular">
                      <Button
                        size="small"
                        variant={advancedOpen ? 'contained' : 'outlined'}
                        color="primary"
                        onClick={() => setAdvancedOpen((current) => !current)}
                        disabled={disabled}
                        startIcon={<Tune fontSize="small" />}
                        endIcon={<KeyboardArrowDown fontSize="small" />}>
                        {advancedOpen ? 'Menos filtros' : 'Mais filtros'}
                      </Button>
                    </Badge>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>

            {activeChips.length > 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                }}>
                {activeChips.map((chip) => (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    onDelete={chip.onDelete}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            ) : null}

            <Collapse in={advancedOpen} timeout="auto" unmountOnExit>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={1.5}>
                {advancedFilters.map((filter) => (
                  <Grid item xs={12} sm={6} md={3} key={filter.code}>
                    <FormControl fullWidth size="small" disabled={disabled}>
                      <InputLabel id={`${filter.code}-label`}>
                        {filter.label}
                      </InputLabel>
                      <Select
                        labelId={`${filter.code}-label`}
                        id={`${filter.code}-select`}
                        label={filter.label}
                        disabled={filter.options.length === 0 || disabled}
                        value={state[filter.code as keyof State]}
                        onChange={(event) =>
                          handleFilterChange(
                            filter.code,
                            event.target.value as State[keyof State],
                          )
                        }>
                        {!NOT_NULL_FILTERS.includes(filter.code) && (
                          <MenuItem value="">Selecione</MenuItem>
                        )}
                        {filter.options.map(({ label, value }) => (
                          <MenuItem
                            key={String(value)}
                            value={value as unknown as string}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ))}
              </Grid>
            </Collapse>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}
